import React, { Component } from 'react';
import { isEqual } from 'lodash';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { AbstractChart } from 'containers/charts/AbstractChart';
import { formatNumbers } from 'helpers/formatNumbers';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import helpers from 'helpers';


const ChartWrapper = styled.div`
    width: 100%;
    overflow: hidden;
    position: relative;
`;

const TotalWrapper = styled.div`
    position: absolute;
    width: 100%;
    height: ${props => props.chartHeight};
    top: 10px;
    left: 0px;
    color:  ${props => props.theme.charts.titleColor};
    display: ${props => (props.chartHeight ? 'flex' : 'none')};
    overflow: hidden;
    flex-direction: column;
    pointer-events: none;
    justify-content: center;
    align-items: center;
`;

const TotalValue = styled.div`
    font-size: 22px;
`;

const TotalTitle = styled.div`
    font-size: 14px;
    letter-spacing: 1px;
`;

class DonutChart extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chartHeight: null
        };
        this.chartRef = React.createRef();
        this.adjustTotalsHeight = this.adjustTotalsHeight.bind(this);
    }

    adjustTotalsHeight(chart) {
        this.setState({ chartHeight: `${chart.target.plotBox.height}px` });
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps.data, this.props.data) ||
            !isEqual(nextProps.total, this.props.total) ||
            !isEqual(nextState.chartHeight, this.state.chartHeight);
    }

    render() {
        const { orderedColors } = this.props.theme;
        const { chartRef } = this;
        const { data, hideCSVExport } = this.props;
        const totalTitle = String(this.props.totalTitle).toUpperCase();
        const totalValue = formatNumbers(this.props.total, this.props.totalFormat);
        const averageNameLength = data.reduce((sum, el) => sum + (el.name ? el.name.length : 0), 0) / data.length;
        const nameLengthLimit = 20;
        const hasLongNames = averageNameLength >= nameLengthLimit;
        const seriesData = data.map((element, index) => {
            if (!element.color) {
                element.color = orderedColors[index % orderedColors.length];
            }
            return element;
        });

        const plotOptions = {
            pie: {
                size: '100%',
                innerSize: '65%',
                showInLegend: true,
                allowPointSelect: true,
                dataLabels: {
                    enabled: true,
                    format: '{point.percentage:.0f}%',
                    distance: -26,
                    filter: {
                        property: 'percentage',
                        operator: '>',
                        value: 3
                    },
                    style: {
                        fontSize: '13px',
                        textOutline: false,
                        color: '#fff'
                    }
                },
                events: {
                    afterAnimate() {
                        const chart = this.chart;
                        const legend = chart.legend;
                        const tooltip = this.chart.tooltip;
                        legend.allItems.forEach((item) => {
                            item.legendItem.on('mouseover', () => {
                                const tooltipData = item.series.data[item.index];
                                tooltip.refresh(tooltipData);
                            }).on('mouseout', () => {
                                tooltip.hide();
                            });
                        });
                    }
                }
            }
        };

        return (
            <ContentTile>
                <ContentHeader hideCSVExport={hideCSVExport} chartRef={chartRef} type={'chart'}>{this.props.title}</ContentHeader>
                <ChartWrapper>
                    <TotalWrapper chartHeight={this.state.chartHeight}>
                        <TotalValue>{totalValue}</TotalValue>
                        <TotalTitle>{totalTitle}</TotalTitle>
                    </TotalWrapper>
                    <AbstractChart
                        ref={chartRef}
                        exportName={this.props.exportName}
                        config={{
                            meta: {
                                title: this.props.title,
                                totalValue,
                                totalTitle
                            },
                            title: {
                                text: null
                            },
                            chart: {
                                type: 'pie',
                                plotBackgroundColor: null,
                                backgroundColor: 'rgba(255, 255, 255, 0.0)',
                                plotBorderWidth: null,
                                plotShadow: false,
                                style: {
                                    fontFamily: 'Lato'
                                },
                                events: {
                                    load: this.adjustTotalsHeight,
                                    redraw: this.adjustTotalsHeight,
                                }
                            },
                            exporting: helpers.commonExportingConfig(this.props.exportName),
                            tooltip: {
                                pointFormat: '{series.name}: <b>{point.percentage:.2f}%</b>'
                            },
                            credits: {
                                enabled: false
                            },
                            legend: {
                                itemStyle: {
                                    color: this.props.theme.colors.darkgray,
                                    fontSize: hasLongNames ? '10px' : '12px'
                                },
                                // set to default width if the names are shorter
                                itemWidth: hasLongNames ? 250 : null,
                            },

                            plotOptions,
                            responsive: {
                                rules: [{
                                    condition: {
                                        maxWidth: 600,
                                    },
                                    chartOptions: {
                                        legend: {
                                            itemWidth: hasLongNames ? 160 : null,
                                        },
                                    }
                                }, {
                                    condition: {
                                        maxWidth: 400,
                                    },
                                    chartOptions: {
                                        legend: {
                                            itemWidth: hasLongNames ? 120 : null,
                                        },
                                    }
                                }]
                            },
                            series: [{
                                name: `${helpers.convertToDisplayValue(this.props.y)}`,
                                data: seriesData
                            }]
                        }} />
                </ChartWrapper>
            </ContentTile>
        );
    }
}

DonutChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        y: PropTypes.number.isRequired,
        color: PropTypes.string
    })).isRequired,
    title: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    totalTitle: PropTypes.string.isRequired,
    totalFormat: PropTypes.string.isRequired,
    exportName: PropTypes.string.isRequired,
    y: PropTypes.string.isRequired
};

export default withTheme(DonutChart);



// WEBPACK FOOTER //
// ./src/components/molecules/charts/DonutChart.js